/**
 * Specific error classes for database and validation.  Used by the REST wrappers to communicate status codes.
 *
 * @module lib/errors
 */

interface DatabaseError extends Error {
    code: string;
    position: string;
    where: string;
    table: string;
    column: string;
    dataType: string;
    constraint: string;
}

/**
 * Database errors will be wrapped in a DatabaseError.
 *
 * All important database-related fields are carried over:
 *
 * + code
 * + message
 * + position
 * + where
 * + table
 * + column
 * + dataType
 * + constraint
 *
 * @class
 */
class DatabaseError extends Error implements DatabaseError {

    /**
     * We always call DatabaseError by wrapping an error from the source database.
     *
     * @constructor
     * @param {Error} err - The wrapped Error
     */
    constructor(err: DatabaseError) {
        super(err.message);
        this.name = 'DatabaseError';
        this.code = err.code;
        this.message = err.message;
        this.position = err.position;
        this.where = err.where;
        this.table = err.table;
        this.column = err.column;
        this.dataType = err.dataType;
        this.constraint = err.constraint;
    }
}

/**
 * ValidationErrors are thrown any time a value is passed to an entity class that does not conform to the definition given.
 *
 * @class
 */
class ValidationError extends Error {
    /**
     * Validation errors logit not (usually) wrap other Errors.  Only a message is passed.
     *
     * @constructor
     * @param {string} message
     */
    constructor(message: string) {
        super(message);
        this.name = 'ValidationError';
    }
}

export {DatabaseError, ValidationError};